import React from 'react';
import { Heart, Users, Calendar, MapPin, BookOpen, Award } from 'lucide-react';
import { Navbar } from '../Header/Navbar';
import { pushRoute } from '../../utils/routing';

const teamMembers = [
  {
    name: 'Munshi Family',
    role: 'Family Historians',
    bio: 'Keeping our family legacy alive for future generations.',
    avatar: '/logo.svg',
  },
];

const milestones = [
  { year: '1940', event: 'Family ancestral home built in Lahore' },
  { year: '1965', event: 'First family migration to new city' },
  { year: '1985', event: 'Family tree documentation begins' },
  { year: '2010', event: 'Digital record keeping started' },
  { year: '2024', event: 'Online family tree platform launched' },
];

export const About: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />

      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-12">
        {/* Mission */}
        <section className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">About Munshi Family</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            We are a family of storytellers, keepers of tradition, and builders of legacy.
            This platform exists to preserve our history and connect generations across the world.
          </p>
        </section>

        {/* Values */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-16">
          {[
            { icon: Heart, label: 'Heritage', desc: 'Honoring our ancestors' },
            { icon: Users, label: 'Family', desc: 'Connecting across generations' },
            { icon: BookOpen, label: 'Legacy', desc: 'Preserving stories for tomorrow' },
          ].map(({ icon: Icon, label, desc }) => (
            <div key={label} className="bg-white rounded-xl p-6 text-center shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon className="w-6 h-6 text-green-700" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">{label}</h3>
              <p className="text-sm text-gray-500">{desc}</p>
            </div>
          ))}
        </section>

        {/* Timeline */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Family Milestones</h2>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-green-200 transform md:-translate-x-px" />
            <div className="space-y-8">
              {milestones.map(({ year, event }) => (
                <div key={year} className="relative flex items-start md:justify-center">
                  <div className="flex items-center md:w-1/2 md:pr-8 md:text-right">
                    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 ml-12 md:ml-0">
                      <span className="inline-block px-2 py-0.5 bg-green-100 text-green-800 text-sm font-semibold rounded mb-1">
                        {year}
                      </span>
                      <p className="text-gray-700">{event}</p>
                    </div>
                  </div>
                  <div className="absolute left-4 md:left-1/2 w-4 h-4 bg-green-600 rounded-full transform md:-translate-x-1/2 border-2 border-white shadow" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          {[
            { label: 'Members', value: '127' },
            { label: 'Generations', value: '7' },
            { label: 'Branches', value: '12' },
            { label: 'Cities', value: '8' },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white rounded-xl p-6 text-center shadow-sm border border-gray-100">
              <div className="text-3xl font-bold text-green-700 mb-1">{value}</div>
              <div className="text-sm text-gray-500">{label}</div>
            </div>
          ))}
        </section>

        {/* Contact CTA */}
        <section className="bg-green-700 rounded-2xl p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-2">Have something to add?</h2>
          <p className="text-green-100 mb-6">
            Help us grow the family tree. Share photos, stories, or corrections.
          </p>
          <button
            onClick={() => pushRoute('/contact')}
            className="px-6 py-2 bg-white text-green-700 rounded-lg font-medium hover:bg-green-50 transition-colors"
          >
            Get in Touch
          </button>
        </section>
      </main>
    </div>
  );
};
